import {assert} from 'Asserts';
import {isMixtureSchema} from 'constants/RegistryConstants';
import {ClientId} from 'notebook/ELNConstants';
import IngredientStructuredTableRowUpdater from 'notebook/integrations/registry/IngredientStructuredTableRowUpdater';
import {isAmountQs, NumericString} from 'notebook/integrations/registry/MixtureGroupConstants';
import * as ContainerSourceTypeCustomEval from 'notebook/integrations/samples/ContainerSourceTypeCustomEval';
import {
  ColumnDefinition,
  ExistingBioentityColumnDefinition,
  INGREDIENT_CATALOG_IDENTIFIER_COLUMN_NAME,
  INGREDIENT_COMPONENT_LOT_CONTAINER_COLUMN_NAME,
  INGREDIENT_COMPONENT_LOT_ENTITY_COLUMN_NAME,
  INGREDIENT_COMPONENT_LOT_TEXT_COLUMN_NAME,
  INGREDIENT_NOTES_COLUMN_NAME,
  UNITS_COLUMN_NAME,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import isMissingFieldColumn from 'notebook/structured-tables/columns/isMissingFieldColumn';
import isReadOnly from 'notebook/structured-tables/columns/isReadOnly';
import {
  isMeasurementAmountStructuredTableColumn,
  isUnitSelectorStructuredTableColumn,
} from 'notebook/structured-tables/columns/TypePredicates';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {
  BaseStructuredTable,
  CellErrorPosition,
  EvalRowView,
  EvalTableView,
  GetCustomEvalArgsOptions,
  PrimarySample,
  PrimarySampleLookupType,
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableColumn,
  StructuredTableColumnGroups,
  TableValidationFn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {
  CustomEvalFnAndType,
  CustomEvalResult,
  CustomEvalType,
} from 'notebook/tables/constants/CustomEvalConstants';
import {RowMetadata} from 'notebook/tables/constants/RowConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {TableReorderLocks} from 'notebook/tables/constants/TableConstants';
import {CellIndex, TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {ResolveQueryFn, ResolveTypedValueToTypeFn} from 'notebook/tables/typed-values/TypedValueResolution';
import {StringTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import computeInheritedTargetAmount from 'registry/mixtures/computeInheritedTargetAmount';
import {
  DEFAULT_MIXTURE_AMOUNT,
  ORDERED_INGREDIENT_UNITS_FOR_DISPLAY,
} from 'registry/mixtures/MixtureConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {Optional} from 'util/Types';

export const COMPONENT_BIOENTITY_COLUMN_NAME = 'Component';
export const TARGET_AMOUNT_COLUMN_NAME = 'Target Amount';
export const MEASURED_AMOUNT_COLUMN_NAME = 'Measured Amount';

export const COMPONENT_BIOENTITY_COLUMN_INDEX = 0;
export const CATALOG_IDENTIFIER_COLUMN_INDEX = 1;
export const TARGET_AMOUNT_COLUMN_INDEX = 2;
export const UNITS_COLUMN_INDEX = 3;
export const MEASURED_AMOUNT_COLUMN_INDEX = 4;
export const COMPONENT_LOT_STARTING_INDEX = 5;

type ComponentLotStorageColumnType = TableColumnType.CONTAINERS | TableColumnType.EXISTING_BIOENTITY;

function isInheritedReadOnlyColumn(column: StructuredTableColumn): boolean {
  return (
    column.columnType === TableColumnType.UNIT_SELECTOR ||
    (column.columnType === TableColumnType.EXISTING_BIOENTITY && column.columnGroup == null)
  );
}

function isCatalogIdentifierColumn(column: StructuredTableColumn): boolean {
  return column.columnType === TableColumnType.INGREDIENT_CATALOG_ID;
}

function isComputedTargetAmountColumn(column: StructuredTableColumn): boolean {
  return (
    column.columnType === TableColumnType.MEASUREMENT_AMOUNT &&
    column.columnGroup === StructuredTableColumnGroups.INGREDIENT_TARGET_AMOUNT
  );
}

function isComponentLotStorageColumn(
  column: StructuredTableColumn,
  columnType: ComponentLotStorageColumnType
): boolean {
  return (
    column.columnGroup === StructuredTableColumnGroups.INGREDIENT_COMPONENT_LOT_STORAGE &&
    column.columnType === columnType
  );
}

function isRowInherited(rowMetadata: MixtureIngredientsStructuredTableRowMetadata): boolean {
  return Boolean(rowMetadata.parentIngredientAmount);
}

export function _componentLotEntityCustomEval(
  currentEntityValue: TypedValue,
  columnDefinition: ExistingBioentityColumnDefinition,
  cellDependencies: Array<TypedValue | null>,
  options: MixtureIngredientsStructuredTableCustomEvalOptions,
  resolveFromResolutionQuery: ResolveQueryFn,
  resolveTypedValueToType: ResolveTypedValueToTypeFn
): CustomEvalResult {
  // `cellDependencies` is provided by getCustomEvalArgs
  const containerCellValue = cellDependencies[0];
  if (containerCellValue == null || isTypedValueEmpty(containerCellValue)) {
    // If the container cell is empty, we should use the direct entity value instead of potentially overriding
    // a non-empty value with an empty value from the container
    return {val: currentEntityValue, error: null, locked: false};
  }
  return ContainerSourceTypeCustomEval.getSourceEntityCustomEval({lockIfContainerValIsEmpty: false})(
    currentEntityValue,
    columnDefinition,
    cellDependencies,
    options,
    resolveFromResolutionQuery,
    resolveTypedValueToType
  );
}

function computeTargetAmount(
  currentTargetAmountValue: TypedValue,
  targetAmountColumnDefinition: Optional<ColumnDefinition>,
  cellDependencies: Array<TypedValue | null>,
  {tableMetadata, rowMetadata}: MixtureIngredientsStructuredTableCustomEvalOptions
): CustomEvalResult {
  if (isRowInherited(rowMetadata)) {
    assert(
      tableMetadata.parentMixtureAmount != null,
      'Expected parentMixtureAmount to be present on table metadata if inherited ingredient rows exist'
    );
    const computedValue: StringTypedValue = {
      type: 'STRING',
      rawValue: computeInheritedTargetAmount(
        rowMetadata.parentIngredientAmount!,
        tableMetadata.parentMixtureAmount,
        // If the user hasn't updated the mixture prep amount from the initial default, then
        // tableMetadata.mixtureAmount won't be set
        tableMetadata.mixtureAmount || DEFAULT_MIXTURE_AMOUNT
      ),
    };
    return {val: computedValue, error: null, locked: true};
  }
  return {val: currentTargetAmountValue, error: null, locked: true};
}

function lockCellIfInherited(
  currentValue: TypedValue,
  columnDefinition: Optional<ColumnDefinition>,
  cellDependencies: Array<TypedValue | null>,
  {rowMetadata}: MixtureIngredientsStructuredTableCustomEvalOptions
): CustomEvalResult {
  return {val: currentValue, error: null, locked: isRowInherited(rowMetadata)};
}

function lockCellIfCatalogIdentifierInherited(
  currentValue: TypedValue,
  columnDefinition: Optional<ColumnDefinition>,
  cellDependencies: Array<TypedValue | null>,
  {rowMetadata}: MixtureIngredientsStructuredTableCustomEvalOptions
): CustomEvalResult {
  return {val: currentValue, error: null, locked: Boolean(rowMetadata.isCatalogIdentifierInherited)};
}

interface MixtureIngredientsStructuredTableCustomEvalOptions {
  tableMetadata: MixtureIngredientsStructuredTableMetadata;
  rowMetadata: MixtureIngredientsStructuredTableRowMetadata;
}

export interface MixtureIngredientsStructuredTableRowMetadata extends RowMetadata {
  // Set by the server after a new ingredient row is processed
  ingredientId?: string | null;
  // If non-null, this indicates that the ingredient for this row is inherited, and is used in the target
  // amount computation
  parentIngredientAmount?: NumericString | null;
  // If non-null, this indicates that the ingredient for this row was copied from an ingredient on the parent
  // mixture (but may not indicate that it's inherited)
  parentIngredientId?: string | null;
  isCatalogIdentifierInherited?: boolean;
}

/**
 * Agent keep in sync: benchling/notebook/structured_tables/constants/mixture_tables.py
 */
export interface MixtureIngredientsStructuredTableMetadata {
  searchAllSchemas: boolean;
  // These amounts are used to calculate the inherited target amounts for inherited ingredients
  mixtureAmount: Optional<NumericString>;
  parentMixtureAmount: Optional<NumericString>;
  parentMixtureId?: string | null;
}

function atMostOneQS(
  table: StructuredTable<
    MixtureIngredientsStructuredTableMetadata,
    MixtureIngredientsStructuredTableRowMetadata
  >,
  computedTable: Array<Array<EvaluatedCell>>
): Array<CellErrorPosition> {
  return table.columns
    .map((column, columnIndex) => {
      let errors: Array<CellErrorPosition> = [];
      if (column.columnType === TableColumnType.MEASUREMENT_AMOUNT) {
        const qsCellsWithRowIndexes = computedTable
          .map((row, rowIndex) => ({rowIndex, cell: row[columnIndex]}))
          .filter(
            (cellWithRowIndex) =>
              cellWithRowIndex.cell.value.type === 'STRING' &&
              isAmountQs(cellWithRowIndex.cell.value.rawValue)
          );
        if (qsCellsWithRowIndexes.length > 1) {
          errors = qsCellsWithRowIndexes.map((cellWithRowIndex) => ({
            rowIndex: cellWithRowIndex.rowIndex,
            columnIndex,
            error: {
              type: 'Error',
              message: 'At most 1 ingredient per mixture can have amount "QS"',
            },
          }));
        }
      }
      return errors;
    })
    .flat();
}

export default class MixtureIngredientsStructuredTableAdapter extends GenericStructuredTableAdapter<
  MixtureIngredientsStructuredTableMetadata,
  MixtureIngredientsStructuredTableRowMetadata
> {
  getDisplayName(
    _: StructuredTable<
      MixtureIngredientsStructuredTableMetadata,
      MixtureIngredientsStructuredTableRowMetadata
    >
  ): string {
    return 'Add mixture prep ingredients';
  }

  override allowSingleInputs(_: MixtureIngredientsStructuredTable): boolean {
    return false;
  }

  getBlankRowMetadata(
    tableMetadata: MixtureIngredientsStructuredTableMetadata
  ): MixtureIngredientsStructuredTableRowMetadata {
    return {
      ingredientId: null,
      parentIngredientAmount: null,
      parentIngredientId: null,
    };
  }

  /**
   * Check if a specific cell position is read-only in the mixture ingredients table.
   *
   * Agent keep in sync: backend function
   * is_mixture_ingredients_table_cell_locked_conservative in mixture_ingredients_table_edits.py
   * (and related code in this file).
   */
  override isPositionReadOnly(
    currentColumnIndex: number,
    row: EvalRowView<MixtureIngredientsStructuredTableRowMetadata>,
    structuredTable: MixtureIngredientsStructuredTable
  ): boolean {
    const {columns, schemas} = structuredTable;
    const column = columns[currentColumnIndex];
    if (isReadOnly(column) || isMissingFieldColumn(column, schemas)) {
      return true;
    }
    if (isComponentLotStorageColumn(column, TableColumnType.CONTAINERS)) {
      const componentLotEntityColumnIndex = columns.findIndex((column) =>
        isComponentLotStorageColumn(column, TableColumnType.EXISTING_BIOENTITY)
      );
      // Lock the component lot container cell if it's empty and there is already a value for the component
      // lot entity
      return (
        componentLotEntityColumnIndex !== -1 && // It's possible to delete this column
        !TableUtil.isCellEmpty(row.cells[componentLotEntityColumnIndex]) &&
        TableUtil.isCellEmpty(row.cells[currentColumnIndex])
      );
    }
    return false;
  }

  override injectFrontEndMetadata({
    column,
    schemas,
    tableMetadata,
  }: {
    column: StructuredTableColumn;
    schemas: Array<SchemaAndColumnGroup>;
    tableMetadata: MixtureIngredientsStructuredTableMetadata;
  }): StructuredTableColumn {
    if (isUnitSelectorStructuredTableColumn(column)) {
      return {
        ...column,
        frontEndMetadata: {acceptedUnits: ORDERED_INGREDIENT_UNITS_FOR_DISPLAY},
      };
    }
    if (isMeasurementAmountStructuredTableColumn(column)) {
      return {
        ...column,
        frontEndMetadata: {
          isComputed: column.name === TARGET_AMOUNT_COLUMN_NAME,
          allowQS: true,
        },
      };
    }
    return column;
  }

  override _getAllSupportedColumns({
    schemas,
  }: {
    entryId: string;
    metadata: MixtureIngredientsStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const componentBioentityColumn = makeStructuredTableColumn({
      columnType: TableColumnType.EXISTING_BIOENTITY,
      name: COMPONENT_BIOENTITY_COLUMN_NAME,
      isRequired: true,
    });
    const catalogIdentifierColumn = makeStructuredTableColumn({
      columnType: TableColumnType.INGREDIENT_CATALOG_ID,
      name: INGREDIENT_CATALOG_IDENTIFIER_COLUMN_NAME,
    });
    const targetAmountColumn = makeStructuredTableColumn({
      columnType: TableColumnType.MEASUREMENT_AMOUNT,
      name: TARGET_AMOUNT_COLUMN_NAME,
      columnGroup: StructuredTableColumnGroups.INGREDIENT_TARGET_AMOUNT,
      isReadOnly: true,
    });
    const unitsColumn = makeStructuredTableColumn({
      columnType: TableColumnType.UNIT_SELECTOR,
      name: UNITS_COLUMN_NAME,
      isRequired: true,
    });
    const measuredAmountColumn = makeStructuredTableColumn({
      columnType: TableColumnType.MEASUREMENT_AMOUNT,
      columnGroup: StructuredTableColumnGroups.INGREDIENT_MEASURED_AMOUNT,
      name: MEASURED_AMOUNT_COLUMN_NAME,
    });
    const componentLotTextColumn = makeStructuredTableColumn({
      columnType: TableColumnType.INGREDIENT_COMPONENT_LOT_TEXT,
      name: INGREDIENT_COMPONENT_LOT_TEXT_COLUMN_NAME,
    });
    const componentLotContainerColumn = makeStructuredTableColumn({
      columnType: TableColumnType.CONTAINERS,
      columnGroup: StructuredTableColumnGroups.INGREDIENT_COMPONENT_LOT_STORAGE,
      name: INGREDIENT_COMPONENT_LOT_CONTAINER_COLUMN_NAME,
    });
    const componentLotBioentityColumn = makeStructuredTableColumn({
      columnType: TableColumnType.EXISTING_BIOENTITY,
      columnGroup: StructuredTableColumnGroups.INGREDIENT_COMPONENT_LOT_STORAGE,
      name: INGREDIENT_COMPONENT_LOT_ENTITY_COLUMN_NAME,
    });
    const notesColumn = makeStructuredTableColumn({
      columnType: TableColumnType.INGREDIENT_NOTES,
      name: INGREDIENT_NOTES_COLUMN_NAME,
    });

    assert(schemas.length === 1, 'Expected exactly 1 schema for mixture ingredients structured table');
    const [schema] = schemas;
    assert(isMixtureSchema(schema), 'Expected mixture schema in mixture ingredients structured table');
    const visibleComponentLotColumns = [];
    if (schema.mixtureSchemaConfig.componentLotTextEnabled) {
      visibleComponentLotColumns.push(componentLotTextColumn);
    }
    if (schema.mixtureSchemaConfig.componentLotStorageEnabled) {
      visibleComponentLotColumns.push(componentLotContainerColumn, componentLotBioentityColumn);
    }

    return [
      componentBioentityColumn,
      catalogIdentifierColumn,
      targetAmountColumn,
      unitsColumn,
      measuredAmountColumn,
      ...visibleComponentLotColumns,
      notesColumn,
    ];
  }

  /**
   * All component lot columns and the Notes column should be editable, meaning they can be deleted and
   * re-ordered. This is necessary since users may need to add/remove component lot columns when the schema
   * settings change, and useful because users may wish to de-clutter the table by removing unused optional
   * columns.
   *
   * User-created lookup columns must also be editable so they can always be deleted after being added when
   * lookup columns are enabled (see {@link enableLookupColumns}).
   *
   * @param column - StructuredTableColumn to determine editability of
   * @returns - whether the column can be edited on a MixtureIngredientsStructuredTable
   */
  override isColumnUserConfigurable(
    _: MixtureIngredientsStructuredTable,
    column: StructuredTableColumn | null
  ): boolean {
    if (column?.columnGroup === StructuredTableColumnGroups.INGREDIENT_COMPONENT_LOT_STORAGE) {
      return true;
    }

    const type = column?.columnType;
    if (type != null) {
      return [
        TableColumnType.INGREDIENT_COMPONENT_LOT_TEXT,
        TableColumnType.INGREDIENT_NOTES,
        TableColumnType.DEDICATED_LOOKUP,
        TableColumnType.CUSTOM,
      ].includes(type);
    }
    return false;
  }

  /**
   * Get the custom eval behavior for a cell.
   *
   * Agent keep in sync: backend function
   * is_mixture_ingredients_table_cell_locked_conservative in mixture_ingredients_table_edits.py
   * (and related code in this file).
   */
  override getTableSpecificCustomEvalFn(
    tableView: EvalTableView<
      MixtureIngredientsStructuredTableMetadata,
      MixtureIngredientsStructuredTableRowMetadata
    >,
    cellIndex: CellIndex
  ): CustomEvalFnAndType<MixtureIngredientsStructuredTableCustomEvalOptions> | null {
    const {column} = this.getRowAndColumnForCellIndex(tableView, cellIndex);
    if (isComputedTargetAmountColumn(column)) {
      return {customEvalFn: computeTargetAmount, customEvalType: CustomEvalType.DEFAULT};
    }
    if (isCatalogIdentifierColumn(column)) {
      return {customEvalFn: lockCellIfCatalogIdentifierInherited, customEvalType: CustomEvalType.DEFAULT};
    }
    if (isInheritedReadOnlyColumn(column)) {
      return {customEvalFn: lockCellIfInherited, customEvalType: CustomEvalType.DEFAULT};
    }
    if (isComponentLotStorageColumn(column, TableColumnType.CONTAINERS)) {
      return {
        customEvalFn: ContainerSourceTypeCustomEval.sourceContainerCustomEval,
        customEvalType: CustomEvalType.DEFAULT,
      };
    }
    if (isComponentLotStorageColumn(column, TableColumnType.EXISTING_BIOENTITY)) {
      return {customEvalFn: _componentLotEntityCustomEval, customEvalType: CustomEvalType.DEFAULT};
    }
    return null;
  }

  override getTableSpecificCustomEvalArgs(
    tableView: EvalTableView<
      MixtureIngredientsStructuredTableMetadata,
      MixtureIngredientsStructuredTableRowMetadata
    >,
    cellIndex: CellIndex,
    options: GetCustomEvalArgsOptions
  ): Array<string> {
    const {toSpreadsheetIndex} = options;
    const {columns} = tableView;
    const {row: currentRowIndex, column: currentColumnIndex} = cellIndex;
    const column = columns[currentColumnIndex];
    if (isComponentLotStorageColumn(column, TableColumnType.EXISTING_BIOENTITY)) {
      const dependencyColumnIndex = columns.findIndex((column) =>
        isComponentLotStorageColumn(column, TableColumnType.CONTAINERS)
      );
      if (dependencyColumnIndex === -1) {
        // It's possible for the user to delete a component lot column from the table
        return [];
      }
      return [
        `=${toSpreadsheetIndex({
          row: currentRowIndex,
          column: dependencyColumnIndex,
        })}`,
      ];
    }
    return [];
  }

  override getTableValidationFns(): Array<
    TableValidationFn<MixtureIngredientsStructuredTableMetadata, MixtureIngredientsStructuredTableRowMetadata>
  > {
    return [...super.getTableValidationFns(), atMostOneQS];
  }

  override shouldSuppressTitleBar(
    _table: StructuredTable<
      MixtureIngredientsStructuredTableMetadata,
      MixtureIngredientsStructuredTableRowMetadata
    >
  ): boolean {
    return true;
  }

  override allowAsTableMappingDestination(
    _table: StructuredTable<
      MixtureIngredientsStructuredTableMetadata,
      MixtureIngredientsStructuredTableRowMetadata
    >
  ): boolean {
    return true;
  }

  override shouldShowFormulaBox(
    _table: StructuredTable<
      MixtureIngredientsStructuredTableMetadata,
      MixtureIngredientsStructuredTableRowMetadata
    >
  ): boolean {
    return true;
  }

  override enableLookupColumns(
    _table: StructuredTable<
      MixtureIngredientsStructuredTableMetadata,
      MixtureIngredientsStructuredTableRowMetadata
    >
  ): boolean {
    return true;
  }

  override enableCustomColumns(
    _table: StructuredTable<
      MixtureIngredientsStructuredTableMetadata,
      MixtureIngredientsStructuredTableRowMetadata
    >
  ): boolean {
    return true;
  }

  /**
   * Set the existing ingredient for each row, if one exists, as the primary sample.
   *
   * @param structuredTable
   * @param computedTable
   */
  // Agent keep in sync: MixtureIngredientsTableInstantiator.get_primary_sample_ids_for_row in
  // benchling/notebook/structured_tables/instantiators/mixture_ingredients_table_instantiator.py (tested by
  // TestMixtureIngredientsInstantiatorPrimarySamples in
  // tests/unit/notebook/structured_tables/instantiators/mixture_ingredients_table_instantiator_test.py)
  override getPrimarySamples(
    structuredTable: StructuredTable<
      MixtureIngredientsStructuredTableMetadata,
      MixtureIngredientsStructuredTableRowMetadata
    >,
    computedTable?: Array<Array<EvaluatedCell>> | null
  ): {[p: string]: Array<PrimarySample>} {
    const primarySamplesByRowId = {} as {[rowId: string]: Array<PrimarySample>};
    structuredTable.rows.forEach(({rowId, metadata, cells}, rowIndex) => {
      const primarySamples: Array<PrimarySample> = [];
      if (metadata.ingredientId) {
        primarySamples.push({
          schemaId: null,
          columnGroup: null,
          sampleLookupInfo: {
            sampleType: PrimarySampleLookupType.DIRECT,
            idForLookup: metadata.ingredientId,
          },
        });
      }
      primarySamplesByRowId[rowId] = primarySamples;
    });
    return primarySamplesByRowId;
  }

  override getTableKindRowUpdater(): typeof IngredientStructuredTableRowUpdater {
    return IngredientStructuredTableRowUpdater;
  }

  override allowReorderDimensions(
    structuredTable: StructuredTable<
      MixtureIngredientsStructuredTableMetadata,
      MixtureIngredientsStructuredTableRowMetadata
    >
  ): TableReorderLocks {
    return {
      [TableDimension.ROW]: false,
      [TableDimension.COLUMN]: true,
    };
  }
}

export type GenericMixtureIngredientsStructuredTable<TIds> = BaseStructuredTable<
  MixtureIngredientsStructuredTableMetadata,
  MixtureIngredientsStructuredTableRowMetadata,
  TIds
>;
export type MixtureIngredientsStructuredTable = GenericMixtureIngredientsStructuredTable<ClientId>;
